(() => {
  const elementName = 'finch-featured-video-collections';

  if (customElements.get(elementName)) return;

  class FinchFeaturedVideoCollections extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.isInitialized = true;
      this.onClick = this.handleClick.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.initializeYoutubePosters();
      this.initializeTabs();
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.isInitialized = false;
    }

    get tabs() {
      return Array.from(this.querySelectorAll('[data-ffvc-tab]'));
    }

    get contentPanels() {
      return Array.from(this.querySelectorAll('[data-ffvc-content]'));
    }

    get mediaPanels() {
      return Array.from(this.querySelectorAll('[data-ffvc-media]'));
    }

    initializeYoutubePosters() {
      this.querySelectorAll('[data-ffvc-youtube-poster]').forEach((image) => {
        const fallbackSource = image.getAttribute('data-ffvc-youtube-fallback');

        if (!fallbackSource) return;

        const useFallback = () => {
          if (image.getAttribute('src') === fallbackSource) return;

          image.setAttribute('src', fallbackSource);
        };
        const verifyResolution = () => {
          if (image.naturalWidth > 0 && image.naturalWidth < 480) useFallback();
        };

        image.addEventListener('error', useFallback, { once: true });
        image.addEventListener('load', verifyResolution, { once: true });

        if (image.complete) {
          if (image.naturalWidth === 0) {
            useFallback();
          } else {
            verifyResolution();
          }
        }
      });
    }

    initializeTabs() {
      const tabs = this.tabs;

      if (tabs.length === 0) return;

      const selectedTab =
        tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ||
        tabs.find((tab, index) => this.hasVisiblePanelForTab(tab, index)) ||
        tabs[0];

      this.activateTab(selectedTab, { pauseOutgoing: false });
    }

    hasVisiblePanelForTab(tab, tabIndex) {
      const blockId = tab.dataset.blockId;
      const hasVisibleMatch = (panels) => panels.some((panel, panelIndex) => {
        return (
          this.panelMatchesSelection(panel, panelIndex, blockId, tabIndex) &&
          !panel.hidden
        );
      });

      return hasVisibleMatch(this.contentPanels) || hasVisibleMatch(this.mediaPanels);
    }

    handleClick(event) {
      const tab = event.target.closest('[data-ffvc-tab]');

      if (!tab || !this.contains(tab)) return;

      this.activateTab(tab);
    }

    handleKeydown(event) {
      const currentTab = event.target.closest('[data-ffvc-tab]');

      if (!currentTab || !this.contains(currentTab)) return;

      const tabs = this.tabs;
      const currentIndex = tabs.indexOf(currentTab);
      let nextIndex;

      switch (event.key) {
        case 'ArrowDown':
          nextIndex = (currentIndex + 1) % tabs.length;
          break;
        case 'ArrowUp':
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.activateTab(tabs[nextIndex], { focus: true });
    }

    handleBlockSelect(event) {
      const eventBlock = event.target.closest?.('[data-block-id]');
      const blockId = String(event.detail?.blockId || eventBlock?.dataset.blockId || '');

      if (!blockId) return;

      const tab = this.tabs.find((candidate) => candidate.dataset.blockId === blockId);

      if (tab) this.activateTab(tab);
    }

    activateTab(tab, options = {}) {
      const tabs = this.tabs;
      const selectedIndex = tabs.indexOf(tab);

      if (selectedIndex === -1) return;

      const selectedBlockId = tab.dataset.blockId;
      const { focus = false, pauseOutgoing = true } = options;

      tabs.forEach((candidate, index) => {
        const isSelected = index === selectedIndex;

        candidate.setAttribute('aria-selected', String(isSelected));
        candidate.tabIndex = isSelected ? 0 : -1;
      });

      this.updatePanels(
        this.contentPanels,
        selectedBlockId,
        selectedIndex,
        pauseOutgoing
      );
      this.updatePanels(
        this.mediaPanels,
        selectedBlockId,
        selectedIndex,
        pauseOutgoing
      );

      if (focus) tab.focus();
    }

    updatePanels(panels, selectedBlockId, selectedIndex, pauseOutgoing) {
      panels.forEach((panel, panelIndex) => {
        const isSelected = this.panelMatchesSelection(
          panel,
          panelIndex,
          selectedBlockId,
          selectedIndex
        );

        if (!isSelected && pauseOutgoing) this.pauseMedia(panel);

        panel.hidden = !isSelected;
      });
    }

    panelMatchesSelection(panel, panelIndex, selectedBlockId, selectedIndex) {
      if (selectedBlockId && panel.dataset.blockId) {
        return panel.dataset.blockId === selectedBlockId;
      }

      return panelIndex === selectedIndex;
    }

    pauseMedia(panel) {
      panel.querySelectorAll('video, audio').forEach((media) => {
        if (typeof media.pause === 'function') media.pause();
      });

      panel.querySelectorAll('iframe').forEach((iframe) => {
        const source = iframe.getAttribute('src') || iframe.dataset.src || '';
        let hostname = '';

        try {
          hostname = new URL(source, document.baseURI).hostname;
        } catch (_error) {
          return;
        }

        if (/(^|\.)youtube(?:-nocookie)?\.com$|(^|\.)youtu\.be$/.test(hostname)) {
          iframe.contentWindow?.postMessage(
            JSON.stringify({
              event: 'command',
              func: 'pauseVideo',
              args: [],
            }),
            '*'
          );
        }

        if (/(^|\.)vimeo\.com$/.test(hostname)) {
          iframe.contentWindow?.postMessage({ method: 'pause' }, '*');
        }
      });
    }
  }

  customElements.define(elementName, FinchFeaturedVideoCollections);
})();
